import React from 'react'
import { format, parseISO } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Label,
} from 'recharts'
import { LoadingIndicator } from 'components'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import idDirectory from './idAttributes'

export interface MultiBarChartProps {
    data: any[]
    isLoading: boolean
    barList: { label: string; accessor: string }[]
    dataKeyXAxis: string
    testId: string
    colorPalette?: string[]
    chartHeight?: number
    noDataChartHeight?: number
    isXAxisDate?: boolean
    dataValuePercent?: boolean
}

const MultiBarChart: React.FC<MultiBarChartProps> = ({
    data = [],
    isLoading,
    barList,
    dataKeyXAxis,
    testId,
    colorPalette,
    chartHeight = 325,
    noDataChartHeight = 335,
    isXAxisDate = true,
    dataValuePercent = false,
}) => {
    const { clientTheme } = useThemeStyling()

    const updatedColorPalette =
        colorPalette ?? clientTheme.charts.pieCharts.defaultPieChart

    // Format percentage values based on their magnitude
    const formatPercentTick = (val: number): string => {
        if (val === 0) {
            return '0%'
        }

        // For small values (less than 1%), show 2 decimal places
        if (val < 1) {
            return `${val.toFixed(2)}%`
        }

        // For larger values, show 1 decimal place
        return `${val.toFixed(1)}%`
    }

    const CustomTooltipValue = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className={'emp-multiBarChart-tooltipContainer'}
                    style={{ fontSize: clientTheme.typography.body1.fontSize }}
                >
                    <table style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#b3b3b3' }}>
                                <td
                                    colSpan={2}
                                    className={
                                        'emp-multiBarChart-tooltipTableHeader'
                                    }
                                >
                                    {label}
                                </td>
                            </tr>
                            {payload.map((item: any, i: number) => {
                                return (
                                    <tr key={`key-tooltip-${i}`}>
                                        <td
                                            className={
                                                'emp-multiBarChart-tooltipTableRow'
                                            }
                                            style={{
                                                borderRight:
                                                    '1px dotted #b3b3b3',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                size="xs"
                                                icon={faSquare}
                                                style={{
                                                    marginRight: '5px',
                                                    color: payload[i].fill,
                                                }}
                                            />
                                            {payload[i].name}
                                        </td>
                                        <td
                                            className={
                                                'emp-multiBarChart-tooltipTableRow'
                                            }
                                            style={{
                                                textAlign: 'right',
                                            }}
                                        >
                                            {dataValuePercent
                                                ? formatPercentTick(
                                                      payload[i].value
                                                  )
                                                : payload[i].value}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )
        }
        return null
    }

    // const WrappedTick = ({ x, y, payload }: any) => {
    //     const words = payload.value.split(' ')
    //     return (
    //         <g transform={`translate(${x},${y + 10})`}>
    //             {words.map((word: string, index: number) => (
    //                 <text
    //                     key={index}
    //                     x={0}
    //                     y={index * 12}
    //                     textAnchor="middle"
    //                     fill="#666"
    //                     fontSize={clientTheme.typography.label.fontSize}
    //                 >
    //                     {word}
    //                 </text>
    //             ))}
    //         </g>
    //     )
    // }

    return (
        <div id={`${idDirectory.divRoot}-${testId}`}>
            {!isLoading ? (
                data.length ? (
                    <ResponsiveContainer
                        id={`${idDirectory.divChart}-${testId}`}
                        height={chartHeight}
                        width="100%"
                    >
                        <BarChart
                            data={data}
                            margin={{
                                right: 10,
                                left: 5,
                                bottom: 20,
                                top: 5,
                            }}
                        >
                            {isXAxisDate ? (
                                <XAxis
                                    dataKey={dataKeyXAxis}
                                    tickFormatter={(date) =>
                                        format(parseISO(date), 'MM/dd')
                                    }
                                    interval={0}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                            ) : (
                                <XAxis
                                    dataKey={dataKeyXAxis}
                                    interval={0}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                            )}
                            {dataValuePercent ? (
                                <YAxis
                                    tickFormatter={formatPercentTick}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                            ) : (
                                <YAxis
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                            )}
                            <Tooltip
                                content={<CustomTooltipValue />}
                                cursor={{ fill: '#f5f5f5' }}
                            />
                            <Legend
                                verticalAlign="bottom"
                                align="center"
                                wrapperStyle={{
                                    fontSize:
                                        clientTheme.typography.body1.fontSize,
                                    left: 30,
                                    color: '#535353',
                                }}
                            />
                            {barList.map((bar, idx) => {
                                if (bar.accessor === dataKeyXAxis) return null
                                return (
                                    <Bar
                                        key={`key-${bar.accessor}-${idx}`}
                                        dataKey={bar.accessor}
                                        name={bar.label}
                                        fill={
                                            updatedColorPalette[
                                                idx % updatedColorPalette.length
                                            ]
                                        }
                                    />
                                )
                            })}
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <ResponsiveContainer
                        id={`${idDirectory.divNoDataChart}-${testId}`}
                        height={noDataChartHeight}
                        width="100%"
                    >
                        <BarChart data={[]}>
                            <XAxis ticks={['No Data']}>
                                <Label
                                    value="No Data"
                                    offset={-160}
                                    position="insideTop"
                                    style={{
                                        fontSize: '14px',
                                    }}
                                />
                            </XAxis>
                            <YAxis
                                tick={{
                                    fontSize:
                                        clientTheme.typography.label.fontSize,
                                }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )
            ) : (
                <div
                    className={'emp-multiBarChart-loadingContainer'}
                    id={`${idDirectory.divLoading}-${testId}`}
                >
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default MultiBarChart
